import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaGraduationCap, 
  FaArrowRight, 
  FaStar, 
  FaTrophy, 
  FaRocket, 
  FaTimes, 
  FaCode,
  FaShieldAlt,
  FaLaptop,
  FaChartLine,
  FaBuilding,
  FaDesktop,
  FaDatabase,
  FaNetworkWired
} from 'react-icons/fa';

const DegreeProgramsAdEnhanced = ({ 
  position = 'top-right', 
  delay = 2000, 
  autoClose = false, 
  autoCloseDelay = 10000,
  showOnPages = ['/'] 
}) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [currentText, setCurrentText] = useState(0);
  const [currentIcon, setCurrentIcon] = useState(0);

  const adTexts = [
    "🎓 Discover Your Future",
    "🚀 Launch Your Career", 
    "💼 Industry-Ready Skills",
    "🏆 Accredited Programs",
    "⭐ World-Class Education",
    "🔬 Cutting-Edge Research",
    "🌍 Global Opportunities",
    "💡 Innovation Hub"
  ];

  const programIcons = [
    FaCode, FaShieldAlt, FaLaptop, FaChartLine, 
    FaBuilding, FaDesktop, FaDatabase, FaNetworkWired
  ];

  const programNames = [
    "Computer Science", "Cybersecurity", "IT", "Data Science",
    "Business", "Web Dev", "Database", "Networking"
  ];

  useEffect(() => {
    // Show ad after specified delay
    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    // Auto-rotate text every 2.5 seconds
    const textTimer = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % adTexts.length);
    }, 2500);

    // Auto-rotate icons every 3 seconds
    const iconTimer = setInterval(() => {
      setCurrentIcon((prev) => (prev + 1) % programIcons.length);
    }, 3000);

    // Auto-close if enabled
    let autoCloseTimer;
    if (autoClose) {
      autoCloseTimer = setTimeout(() => {
        handleClose();
      }, autoCloseDelay);
    }

    return () => {
      clearTimeout(showTimer);
      clearInterval(textTimer);
      clearInterval(iconTimer);
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [delay, autoClose, autoCloseDelay]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
    }, 300);
  };

  const handleClick = () => {
    navigate('/programs');
  };

  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'center': 'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
  };

  if (!isVisible) return null;

  const IconComponent = programIcons[currentIcon];

  return (
    <div className={`fixed ${positionClasses[position]} z-50 transition-all duration-300 ${
      isClosing ? 'opacity-0 scale-95 translate-x-full' : 'opacity-100 scale-100 translate-x-0'
    }`}>
      <div 
        className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 text-white rounded-2xl shadow-2xl cursor-pointer overflow-hidden group hover:shadow-3xl transition-all duration-300"
        onClick={handleClick}
        style={{
          width: '350px',
          height: '220px',
          animation: 'pulse 2s infinite, bounce 3s infinite'
        }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-shimmer"></div>
        
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="absolute top-3 right-3 z-10 bg-black/20 hover:bg-black/40 rounded-full p-1.5 transition-colors"
        >
          <FaTimes className="text-white text-sm" />
        </button>

        {/* Content */}
        <div className="relative p-6 h-full flex flex-col justify-between">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-white/20 p-3 rounded-full group-hover:scale-110 transition-transform">
              <FaGraduationCap className="text-2xl animate-bounce" />
            </div>
            <div>
              <h3 className="font-bold text-xl leading-tight">Degree Programs</h3>
              <p className="text-sm opacity-90">Explore Now!</p>
            </div>
          </div>

          {/* Rotating Text */}
          <div className="mb-4">
            <div 
              key={currentText}
              className="text-2xl font-bold animate-fadeIn"
              style={{
                background: 'linear-gradient(45deg, #fff, #ffd700, #fff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {adTexts[currentText]}
            </div>
          </div>

          {/* Program Icons */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <div className="bg-white/20 p-2 rounded-lg group-hover:scale-110 transition-transform">
                <IconComponent className="text-lg" />
              </div>
              <span className="text-sm font-semibold">{programNames[currentIcon]}</span>
            </div>
          </div>

          {/* Features */}
          <div className="flex items-center gap-4 text-sm mb-3">
            <div className="flex items-center gap-1">
              <FaStar className="text-yellow-300" />
              <span>8+ Programs</span>
            </div>
            <div className="flex items-center gap-1">
              <FaTrophy className="text-yellow-300" />
              <span>95% Success</span>
            </div>
            <div className="flex items-center gap-1">
              <FaRocket className="text-yellow-300" />
              <span>Career Ready</span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">Click to Explore →</span>
            <div className="bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors group-hover:scale-110">
              <FaArrowRight className="text-lg animate-pulse" />
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-4 left-4 w-2 h-2 bg-yellow-300 rounded-full animate-ping"></div>
        <div className="absolute bottom-6 left-8 w-1 h-1 bg-white rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute top-12 right-8 w-1.5 h-1.5 bg-yellow-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
        <div className="absolute bottom-4 right-6 w-1 h-1 bg-white rounded-full animate-ping" style={{animationDelay: '1.5s'}}></div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default DegreeProgramsAdEnhanced;
